import { useState } from "react";
import { useShareGate } from "@/hooks/useShareGate";
import { ToolContainer } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { generateLorem, clampCount, countStats, type ParagraphLength } from "./lorem";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Generates placeholder "lorem ipsum" text, the standard filler text used by designers and developers to mock up a layout before real content is ready — it looks like genuine paragraphs at a glance without carrying any real meaning, which is exactly the point: it doesn't distract from evaluating the actual layout and typography.`,
  how: `Choose how many paragraphs, sentences, or words you need, generate the text, and copy it into your design or document.`,
  tip: `Generating placeholder text at roughly the same length as your real content will give you a much more accurate sense of how a layout will actually look once it's filled in — a layout that looks fine with two short placeholder sentences can break entirely once a real paragraph of the actual expected length goes in.`,
};

export default function LoremIpsum() {
  const { gateShare } = useShareGate();
  const [count, setCount] = useState("5");
  const [type, setType] = useState("paragraphs");
  const [output, setOutput] = useState("");
  const [note, setNote] = useState<string | null>(null);
  const [startLorem, setStartLorem] = useState(true);
  const [length, setLength] = useState("medium");
  const [vocab, setVocab] = useState("random");
  const [htmlWrap, setHtmlWrap] = useState(false);

  const { logUsageNow } = useLogUsageOnAction();

  const generate = async () => {
    const allowed = await logUsageNow();
    if (!allowed) return;
    const { n, note } = clampCount(count);
    if (note) { setNote(note); setCount(String(n)); } else setNote(null);
    const result = generateLorem({ count: n, type: type as "paragraphs" | "sentences" | "words", startWithLorem: startLorem, html: htmlWrap && type === "paragraphs", length: length as ParagraphLength, vocab: vocab as "random" | "classic" });
    setOutput(result);
  };


  return (
    <ToolContainer>
      <div className="flex flex-wrap items-end gap-4 mb-4">
        <div><Label>Count</Label><Input aria-label="Count" type="number" min={1} max={1000} value={count} onChange={e => setCount(e.target.value)} className="w-24" /></div>
        <div><Label>Type</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="paragraphs">Paragraphs</SelectItem>
              <SelectItem value="sentences">Sentences</SelectItem>
              <SelectItem value="words">Words</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {type === "paragraphs" && <div><Label>Paragraph length</Label>
          <Select value={length} onValueChange={setLength}>
            <SelectTrigger className="w-40" aria-label="Paragraph length"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="short">Short (2-3 sentences)</SelectItem>
              <SelectItem value="medium">Medium (4-6)</SelectItem>
              <SelectItem value="long">Long (7-10)</SelectItem>
            </SelectContent>
          </Select>
        </div>}
        <div><Label>Text style</Label>
          <Select value={vocab} onValueChange={setVocab}>
            <SelectTrigger className="w-44" aria-label="Text style"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="random">Random words</SelectItem>
              <SelectItem value="classic">Classic Latin (Cicero)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={generate}>Generate</Button>
      </div>
      <div className="flex flex-wrap items-center gap-6 mb-4">
        <div className="flex items-center gap-2"><Switch checked={startLorem} onCheckedChange={setStartLorem} /><Label className="text-sm">Start with "Lorem ipsum dolor sit amet…"</Label></div>
        {type === "paragraphs" && <div className="flex items-center gap-2"><Switch checked={htmlWrap} onCheckedChange={setHtmlWrap} /><Label className="text-sm">Wrap in &lt;p&gt; tags</Label></div>}
      </div>
      {note && <NoticeBox className="text-sm mb-3" role="status">{note}</NoticeBox>}
      {output && (
        <div className="relative">
          <p className="text-xs text-muted-foreground mb-1">{countStats(output).words} words · {countStats(output).chars} characters</p>
          <pre className="p-4 rounded-lg border bg-card text-sm whitespace-pre-wrap font-sans">{output}</pre>
          <Button size="sm" variant="outline" className="absolute top-2 right-2" onClick={() => { void gateShare(async () => { try { await navigator.clipboard.writeText(output); toast.success("Copied!"); return true; } catch { toast.error("Couldn't copy. Select the text and copy it by hand."); return false; } }); }}>Copy</Button>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
